import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { Typography, CircularProgress } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-pro";

// Force-remove empty/filler cells from DataGrid DOM
const removeEmptyCells = (containerRef) => {
  if (!containerRef?.current) return;
  const el = containerRef.current;
  // Remove filler divs
  el.querySelectorAll('.MuiDataGrid-filler').forEach(node => {
    node.style.display = 'none';
  });
  // Remove empty cells (both class variants)
  el.querySelectorAll('.MuiDataGrid-cellEmpty, .MuiDataGrid-cell--empty, [data-field="__empty__"]').forEach(node => {
    node.style.display = 'none';
  });
  // Remove empty column headers
  el.querySelectorAll('.MuiDataGrid-columnHeader--empty, .MuiDataGrid-columnHeaderEmpty, [data-field="__empty__"]').forEach(node => {
    node.style.display = 'none';
  });
};

const GridTableData = ({
  columnsSet,
  data,
  showColumns,
  type,
  isSeriesScore = false,
  loader,
  activeTab,
  tabIndex,
  disableAutoResize = false,
}) => {
  const apiRef = useGridApiRef();
  const gridContainerRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(1000);
  const [cols, setCols] = useState([]);
  const [isGridReady, setIsGridReady] = useState(false);

  const prevActiveTabRef = useRef(activeTab);
  const prevDataRef = useRef(data);
  const autosizeTimeoutRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const initialLoadRef = useRef(true);

  const fetchRows = useCallback(
    async (page, pageSize, dataSet) => {
      try {
        if (type === 2 && dataSet.length > 0) {
          const updatedArray = dataSet.map((item) => ({
            ...item,
            ...(item.result_options || {}),
          }));
          setRows(updatedArray);
        } else {
          setRows(dataSet);
        }
        setCols(columnsSet);
        setRowCount(dataSet.length);
      } catch (error) {
        console.error("Error fetching rows:", error);
      }
    },
    [type, columnsSet]
  );

  useEffect(() => {
    // Reset grid ready state when data changes (e.g. from search filtering)
    // so the loader shows while columns auto-resize
    if (prevDataRef.current !== data) {
      prevDataRef.current = data;
      if (isGridReady && data.length > 0) {
        setIsGridReady(false);
      }
    }
    fetchRows(page, pageSize, data);
  }, [page, pageSize, data, fetchRows]);

  useEffect(() => {
    if (prevActiveTabRef.current !== activeTab) {
      initialLoadRef.current = true;
      setIsGridReady(false);
      setRows([]);
      setPage(0);
      setPageSize(1000);

      // Clear any pending timeouts
      if (autosizeTimeoutRef.current) {
        clearTimeout(autosizeTimeoutRef.current);
      }
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      fetchRows(0, 1000, data);
      prevActiveTabRef.current = activeTab;
    }
  }, [activeTab, tabIndex, data, fetchRows]);

  const filteredColumns = useMemo(() => {
    if (!cols.length || !rows.length) return [];

    if (isSeriesScore) {
      return cols;
    }

    return cols.filter((column) => {
      const field = column.field;
      return rows.some((row) => {
        return (
          row[field] !== null && row[field] !== undefined && row[field] != 0
        );
      });
    });
  }, [cols, rows, isSeriesScore]);

  // Optimized autosize function
  const autosizeColumns = useCallback((immediate = false) => {
    // Skip autosize if disabled
    if (disableAutoResize) {
      setIsGridReady(true);
      return;
    }

    if (!apiRef.current || rows.length === 0 || activeTab !== tabIndex) return;

    // Clear any existing timeout
    if (autosizeTimeoutRef.current) {
      clearTimeout(autosizeTimeoutRef.current);
    }

    const performAutosize = () => {
      try {
        apiRef.current.autosizeColumns({
          includeHeaders: true,
          includeOutliers: true,
          outliersFactor: 1.5,
          expand: false,
        });

        // Run autosize again after a short delay to catch any late renders
        setTimeout(() => {
          try {
            apiRef.current.autosizeColumns({
              includeHeaders: true,
              includeOutliers: true,
              outliersFactor: 1.5,
              expand: false,
            });
          } catch (e) { }
          setIsGridReady(true);
          initialLoadRef.current = false;
        }, 150);
      } catch (error) {
        console.error("Error autosizing columns:", error);
        setIsGridReady(true);
        initialLoadRef.current = false;
      }
    };

    if (immediate) {
      performAutosize();
    } else {
      autosizeTimeoutRef.current = setTimeout(performAutosize, initialLoadRef.current ? 200 : 250);
    }
  }, [apiRef, rows, activeTab, tabIndex, disableAutoResize]);

  // Initial autosize when rows and columns are ready
  useEffect(() => {
    if (rows.length === 0 || filteredColumns.length === 0) return;

    autosizeColumns();

    return () => {
      if (autosizeTimeoutRef.current) {
        clearTimeout(autosizeTimeoutRef.current);
      }
    };
  }, [rows, filteredColumns, autosizeColumns]);

  // Handle window resize with debouncing
  useEffect(() => {
    const handleResize = () => {
      if (!apiRef.current || rows.length === 0 || activeTab !== tabIndex) return;

      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      resizeTimeoutRef.current = setTimeout(() => {
        autosizeColumns(true);
      }, 300);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [apiRef, rows, activeTab, tabIndex, autosizeColumns]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autosizeTimeoutRef.current) {
        clearTimeout(autosizeTimeoutRef.current);
      }
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  // Force-remove empty/filler cells whenever grid becomes ready or rows change
  useEffect(() => {
    if (!isGridReady || rows.length === 0) return;
    // Run multiple times with increasing delays to catch all renders
    const timers = [50, 150, 400].map(delay =>
      setTimeout(() => removeEmptyCells(gridContainerRef), delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [isGridReady, rows, filteredColumns]);

  const columns = useMemo(() => {
    // Create a canvas to measure header text width
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = "bold 12px Roboto, sans-serif";

    return filteredColumns.map(({ flex, ...col }) => {
      // Calculate minWidth based on header text so it never clips
      const headerText = col.headerName || "";
      const headerTextWidth = Math.ceil(ctx.measureText(headerText).width) + 20;
      const baseMinWidth = col.minWidth || 10;
      const computedMinWidth = Math.max(baseMinWidth, headerTextWidth);

      const isCenterAligned = ["showPlace", "score"].includes(col.field);

      return {
        ...col,
        resizable: false,
        sortable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: isCenterAligned ? "center" : "left",
        headerAlign: isCenterAligned ? "center" : "left",
        cellClassName: "no-ellipsis-cell",
        minWidth: computedMinWidth,
        renderHeader: () => (
          <span
            style={{
              fontWeight: "700 !important",
              fontSize: "12px !important",
              whiteSpace: "nowrap",
              color: "#42566c",
            }}
          // title={col?.headerName}
          >
            {col?.headerName}
          </span>
        ),
        renderCell: col.field === "showPlace"
          ? (params) => (
            <Typography
              variant="p"
              sx={{
                fontWeight: "700 !important",
                color: "#42566c !important",
              }}
            >
              {params.value}
            </Typography>
          )
          : col.field === "score"
            ? (params) => {
              if (params.value === 0 && params.row.casual === true) {
                return "";
              }
              return (
                <Typography
                  variant="p"
                  sx={{
                    fontWeight: "700 !important",
                    color: "#42566c !important",
                  }}
                >
                  {params.value}
                </Typography>
              );
            }
            : col.field &&
              [
                "finishTime",
                "bch",
                "chc",
                "correctedTime",
                "elapsedTime",
                "yardstickCorrectedTime",
                "yardStick",
                "pd",
                "td",
                "etOrd",
              ].includes(col.field)
              ? (params) => {
                if (!params.value) {
                  return "";
                }

                const val = params.value.toString().trim();

                if (!isNaN(val) && Number(val) === 0) {
                  return "";
                }

                if (val === "00:00:00" || val === "0:00:00") {
                  return "";
                }

                return (
                  <Typography
                    // title={col?.headerName}
                    variant="p"
                    sx={{ paddingRight: "0.15rem !important" }}
                  >
                    {val}
                  </Typography>
                );
              }
              : col.renderCell,
      };
    });
  }, [filteredColumns]);

  return (
    <Box
      ref={gridContainerRef}
      className="public-result-datagrid"
      sx={{
        fontSize: "var(--font_size12) !important",
        width: "fit-content",
        maxWidth: "100%",
        position: "relative",
        overflowX: "auto",
      }}
    >
      {/* Loading overlay - positioned absolutely within container.
          Transparent on purpose: the grid itself is already faded out
          (opacity: 0 below) while the columns autosize, so an opaque sheet only
          added a white block over the page background — most visible while
          typing in the search box, which re-runs the autosize on every
          keystroke. Only the spinner should be visible here. */}
      {!isGridReady && rows.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "transparent",
            // backgroundColor: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            height: "100%",
            width: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <DataGridPro
        key={`grid-${activeTab}-${tabIndex}`}
        apiRef={apiRef}
        rows={rows}
        isResizable={false}
        disableExtendRowFullWidth
        disableColumnResize
        disableColumnSelector
        disableColumnMenu
        disableColumnFilter
        disableVirtualization
        sortingMode="server"
        getRowId={type == "1" ? (row) => row.resultId : (row) => row.id}
        columns={columns}
        columnHeaderHeight={28}
        rowCount={rowCount}
        rowHeight={27}
        autoHeight
        loading={loader}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        pageSize={pageSize}
        disableRowSelectionOnClick
        scrollbar
        slots={{
          headerFilterMenu: null,
        }}
        sx={{
          opacity: isGridReady ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
          "& .MuiDataGrid-cell:focus": {
            outline: "none !important",
          },
          "& .MuiDataGrid-cell:focus-within": {
            outline: "none !important",
          },
          "& .MuiDataGrid-columnHeader:focus": {
            outline: "none !important",
          },
          "& .MuiDataGrid-columnHeader:focus-within": {
            outline: "none !important",
          },
          "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
            outline: "none !important",
          },
          "& .MuiDataGrid-columnSeparator": {
            opacity: "1 !important",
          },
          "& .MuiDataGrid-sortIcon ": {
            fill: "currentColor",
          },
          "& .MuiDataGrid-filterIcon": {
            color: "currentColor !important",
          },
          "& .MuiDataGrid-sortIcon": {
            fill: "currentColor",
            "@media (max-width:600px)": {
              fontSize: "10px",
              padding: 0,
              margin: 0,
              position: "absolute",
              top: "10%",
              zIndex: "10",
            },
          },
          "& .MuiDataGrid-footerContainer": {
            display: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "nowrap",
            overflow: "visible",
            textOverflow: "unset",
          },
          "& .MuiDataGrid-columnHeader": {
            overflow: "visible",
          },
          "& .MuiDataGrid-cell": {
            whiteSpace: "nowrap",
            overflow: "visible",
            textOverflow: "unset",
          },
          "& .MuiDataGrid-virtualScroller": {
            overflowX: "auto",
            overflowY: "hidden",
            paddingBottom: "8px",
          },
          "& .no-ellipsis-cell": {
            whiteSpace: "nowrap",
            overflow: "visible",
            textOverflow: "unset",
          },
          // Override noWrap inline styles on Typography so autosize measures full content
          "& .MuiDataGrid-cell .MuiTypography-root": {
            overflow: "visible !important",
            textOverflow: "unset !important",
            whiteSpace: "nowrap !important",
            maxWidth: "none !important",
          },
          "& .MuiDataGrid-columnSeparator .MuiSvgIcon-root": {
            width: "5px",
          },
          // "& .MuiDataGrid-columnHeaderTitleContainer": {
          //   minWidth: "70px !important"
          // },
          // Force grid content to shrink to actual column widths
          "& .MuiDataGrid-virtualScrollerContent": {
            width: "fit-content !important",
            minWidth: "0 !important",
          },
          "& .MuiDataGrid-virtualScrollerRenderZone": {
            width: "fit-content !important",
            minWidth: "0 !important",
          },
          "& .MuiDataGrid-columnHeadersInner": {
            width: "fit-content !important",
          },
          // Kill ALL empty/filler cells aggressively
          "& .MuiDataGrid-filler": {
            display: "none !important",
            width: "0 !important",
            minWidth: "0 !important",
            maxWidth: "0 !important",
            padding: "0 !important",
            border: "none !important",
          },
          "& .MuiDataGrid-cellEmpty, & .MuiDataGrid-cell--empty": {
            display: "none !important",
            width: "0 !important",
            minWidth: "0 !important",
            maxWidth: "0 !important",
            padding: "0 !important",
            border: "none !important",
          },
          "& .MuiDataGrid-columnHeader--empty, & .MuiDataGrid-columnHeaderEmpty": {
            display: "none !important",
            width: "0 !important",
            minWidth: "0 !important",
            maxWidth: "0 !important",
            padding: "0 !important",
            border: "none !important",
          },
          '& [data-field="__empty__"]': {
            display: "none !important",
            width: "0 !important",
            minWidth: "0 !important",
            maxWidth: "0 !important",
            padding: "0 !important",
            border: "none !important",
          },
          "& .MuiDataGrid-row": {
            width: "fit-content !important",
          },
          "& .MuiDataGrid-columnHeaders": {
            width: "fit-content !important",
          },
        }}
      />
    </Box>
  );
};

export default GridTableData;